<template>
  <div class="h-screen bg-base-100 text-base-content flex flex-col">
    <!-- Fixed Header -->
    <ThreadsNav show-back>
      <template #center> 创建角色 </template>
    </ThreadsNav>

    <div class="flex-1 overflow-y-auto">
      <div class="pb-6 pt-2">
        <!-- Basic Information Section -->
        <div class="mb-2">
          <!-- Character Keywords -->

          <div class="flex items-center red-dot ml-6 mb-2">
            <h2 class="text-base font-medium text-base-content">基本信息</h2>
          </div>

          <!-- Gender Selection -->
          <van-cell-group inset class="mb-4">
            <van-cell
              :title="'性别'"
              :value="selectedGender ? useGender(selectedGender) : ''"
              is-link
              @click="showGenderPicker = true"
            />
            <van-field
              v-model="characterKeywords"
              type="textarea"
              :rows="4"
              placeholder="年龄、职业、性格、仪态等关键词"
              label="人物关键词"
            />
          </van-cell-group>

          <div class="flex items-center red-dot ml-6 mb-2 justify-between mr-4">
            <h2 class="text-base font-medium text-base-content">外貌参考</h2>

            <button
              v-if="referenceImage"
              class="btn btn-xs btn-outline"
              @click="referenceImage = null"
            >
              移除参考图
            </button>
          </div>
          <van-cell-group inset class="mb-2">
            <div>
              <img
                v-if="referenceImage"
                :src="referenceImage.url"
                @click="showMediaSelectPopup = true"
                alt="referenceImage"
                class="w-full h-auto rounded-lg mb-4"
              />

              <button
                v-else
                @click="showMediaSelectPopup = true"
                class="w-full border-2 border-dashed bg-base-200 rounded-lg p-6 bg-base-200/30 hover:bg-base-200/50 transition-colors flex flex-col items-center justify-center"
              >
                <i
                  class="iconify mingcute--add-line text-2xl text-base-content/60 mb-2"
                ></i>
                <span class="text-base-content/60 text-sm">选择参考图</span>
              </button>
            </div>
          </van-cell-group>
        </div>

        <!-- Features List -->
        <div class="space-y-3 mb-8 px-5">
          <div class="flex items-center text-sm text-base-content/80">
            <i
              class="iconify mingcute--check-circle-fill text-success mr-3"
            ></i>
            <span>包含20多种维度的文字设定</span>
          </div>
          <div class="flex items-center text-sm text-base-content/80">
            <i
              class="iconify mingcute--check-circle-fill text-success mr-3"
            ></i>
            <span>包含1个带透明层的立绘形象</span>
          </div>
          <div class="flex items-center text-sm text-base-content/80">
            <i
              class="iconify mingcute--check-circle-fill text-success mr-3"
            ></i>
            <span>包含1个基于立绘的放大头像</span>
          </div>
          <div class="flex items-center text-sm text-base-content/80">
            <i
              class="iconify mingcute--check-circle-fill text-success mr-3"
            ></i>
            <span>包含4个方向的像素小人形象</span>
          </div>
        </div>

        <!-- Create Button -->
        <div class="pb-4 flex justify-center">
          <button
            @click="createCharacter"
            class="btn btn-primary btn-wide gap-1 flex items-center"
          >
            <span>创造角色</span>
            <template v-if="userStore.user.freeMateGenerationCount > 0">
              <!-- <div class="line-through">
                <PowerIcon />
                <span >50</span>
              </div> -->
              <span>免费</span>
              <span>
                (剩余{{ userStore.user.freeMateGenerationCount }}次)
              </span>
            </template>
            <template v-else>
              <PowerIcon />
              <span class="ml-2">50</span>
            </template>
          </button>
        </div>
      </div>
    </div>

    <!-- Gender Selection Modal -->
    <ModalSelectOption
      v-model:show="showGenderPicker"
      :options="genderOptions"
      :initial-value="selectedGender"
      @confirm="onGenderConfirm"
    />

    <!-- Media Select Popup -->
    <MediaSelectPopup
      v-model:show="showMediaSelectPopup"
      :max-count="1"
      :multiple="false"
      :upload-type="7"
      media-type="img"
      :enable-crop="true"
      :crop-aspect-ratio="3 / 4"
      replace
      @on-media-selected="handleImageSelected"
    />
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from "vue";
import { showLoadingToast, showFailToast, showConfirmDialog } from "vant";
import ModalSelectOption from "@/components/mobile/common/ModalSelectOption.vue";
import MediaSelectPopup from "@/components/mobile/media/MediaSelectPopup.vue";
import PowerIcon from "@/components/icon/PowerIcon.vue";
import { MediaUploadFileItem } from "@/types/component";
import type { Gender } from "@/types/entity";
import { GenderEnum } from "@/enums/mateEnums";
import { useCreateMateGenerationTask } from "./MateCreateTask";
import { routerBack } from "@/use/go";
import { useGlobalModal } from "@/stores/globalModal";
import { useAllGender, useGender } from "@/use/common";
import { useGlobalUser } from "@/stores/globalUser";
import { sessionStorageGet, sessionStorageSet } from "@/tools/sessionStorage";

const modalStore = useGlobalModal();
const userStore = useGlobalUser();

// Form data
const selectedGender = ref<GenderEnum>();
const characterKeywords = ref("");
const referenceImage = ref<MediaUploadFileItem | null>(null);

// UI state
const showGenderPicker = ref(false);
const showMediaSelectPopup = ref(false);

// Gender options for picker
const genderOptions = useAllGender();

const onGenderConfirm = (value: number) => {
  selectedGender.value = value as Gender;
  showGenderPicker.value = false;
};

const handleImageSelected = (mediaList: MediaUploadFileItem[]) => {
  if (mediaList.length > 0) {
    referenceImage.value = mediaList[0];
    showMediaSelectPopup.value = false;
  }
};

const { startCreateMateGenerationTask } = useCreateMateGenerationTask({});

const createCharacter = async () => {
  if (
    userStore.user.freeMateGenerationCount <= 0 &&
    modalStore.shouldOpenPowerNotEnoughModal({
      spentPowerAmount: 50,
      cb: createCharacter,
    })
  )
    return;

  const loading = showLoadingToast("正在创建角色...");
  try {
    await startCreateMateGenerationTask({
      referenceMediaId: referenceImage.value?.mediaId,
      prompt: characterKeywords.value,
      gender: selectedGender.value,
    });

    loading.close();
    showConfirmDialog({
      message: "任务创建成功！您将会在角色创建成功之后收到通知。",
      confirmButtonText: "好的",
      showCancelButton: false,
    }).then(() => {
      routerBack();
    });
  } catch (error) {
    loading.close();
    showFailToast("创建失败，请重试");
    console.error("Character creation failed:", error);
  }
};

watch([selectedGender, characterKeywords, referenceImage], () => {
  sessionStorageSet("mateCreateAuto", {
    selectedGender: selectedGender.value,
    characterKeywords: characterKeywords.value,
    referenceImage: referenceImage.value,
  });
});

onMounted(() => {
  const mateCreateAuto = sessionStorageGet("mateCreateAuto");
  if (mateCreateAuto) {
    selectedGender.value = mateCreateAuto.selectedGender;
    characterKeywords.value = mateCreateAuto.characterKeywords;
    referenceImage.value = mateCreateAuto.referenceImage;
  }
});
</script>

<style scoped>
/* Custom styles are now handled by DaisyUI theme system */
</style>
